"use client";
import { useMutation } from "@tanstack/react-query";
import { Button, message } from "antd";
import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface FooterProps {
  isCodeWrong: null | boolean;
  reSendOtp: () => any;
}
const timeSVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <g clip-path="url(#clip0_363_4057)">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8.00264 0.665039C3.96002 0.665039 0.664062 3.95633 0.664062 7.99828C0.664062 12.0402 3.96069 15.3355 8.00264 15.3355C12.0446 15.3355 15.3345 12.0402 15.3345 7.99828C15.3345 3.95633 12.0446 0.665039 8.00264 0.665039ZM8.00264 1.99836C8.79086 1.99704 9.5716 2.15134 10.3001 2.45242C11.0285 2.75349 11.6904 3.19541 12.2477 3.75284C12.805 4.31026 13.2468 4.97222 13.5477 5.70075C13.8486 6.42928 14.0027 7.21005 14.0012 7.99828C14.0028 8.78672 13.8488 9.56772 13.5479 10.2965C13.2471 11.0253 12.8054 11.6876 12.2481 12.2453C11.6909 12.8031 11.029 13.2454 10.3005 13.5469C9.57194 13.8483 8.79108 14.0031 8.00264 14.0022C7.21386 14.0032 6.43263 13.8485 5.70369 13.5472C4.97475 13.2458 4.31241 12.8036 3.7546 12.2459C3.19678 11.6882 2.75445 11.026 2.45292 10.2971C2.15139 9.56825 1.99659 8.78706 1.99738 7.99828C1.99738 4.67699 4.68134 1.99836 8.00264 1.99836ZM7.99197 3.32234C7.90385 3.3232 7.81678 3.34153 7.73579 3.37625C7.6548 3.41098 7.5815 3.46142 7.52012 3.52465C7.45875 3.58789 7.41052 3.66266 7.37822 3.74466C7.34593 3.82665 7.33021 3.91423 7.33198 4.00233V7.99828C7.33235 8.08601 7.35003 8.1728 7.384 8.25368C7.41798 8.33456 7.46758 8.40794 7.52998 8.46961L10.1966 11.1376C10.2583 11.201 10.332 11.2516 10.4134 11.2863C10.4949 11.321 10.5824 11.3391 10.6709 11.3396C10.7594 11.3401 10.8471 11.323 10.9289 11.2893C11.0108 11.2555 11.085 11.2058 11.1474 11.143C11.2098 11.0803 11.2591 11.0057 11.2924 10.9237C11.3257 10.8417 11.3423 10.7539 11.3413 10.6654C11.3403 10.5769 11.3217 10.4895 11.2865 10.4082C11.2514 10.327 11.2004 10.2536 11.1366 10.1923L8.66663 7.72228V4.00233C8.66842 3.91299 8.65224 3.8242 8.61903 3.74124C8.58583 3.65828 8.53629 3.58284 8.47336 3.51941C8.41042 3.45597 8.33537 3.40584 8.25268 3.37199C8.16998 3.33813 8.08132 3.32125 7.99197 3.32234Z"
        fill="#9999A7"
      />
    </g>
    <defs>
      <clipPath id="clip0_363_4057">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
const Footer: FC<FooterProps> = ({ isCodeWrong, reSendOtp }) => {
  const intl = useIntl();
  const { mutate, isLoading } = useMutation(reSendOtp, {
    onSuccess: (res: any) => {
      if (res?.code !== "ERR_BAD_REQUEST" && res?.code !== "ERR_BAD_RESPONSE") {
        message.success(
          intl.formatMessage({ id: "OTPsent" }) || "تم ارسال كود التحقق"
        );
        resetTimer();
      } else {
        message.error(res?.response?.data?.message || "حدث خطأ");
      }
    },
    onError: (err: any) => {
      err?.response?.data[0] ||
        message.error(err?.response?.data?.message || "حدث خطأ");
    },
  });
  const [time, setTime] = useState(60);
  const [isRunning, setIsRunning] = useState(false);
  let intervalId: any;
  const startTimer = () => {
    setIsRunning(true);
    intervalId = setInterval(() => {
      if (time > 0) {
        setTime((prevTime) => prevTime - 1);
      } else {
        setIsRunning(false);
        clearInterval(intervalId);
      }
    }, 1000);
    return () => clearInterval(intervalId); // Cleanup function for the interval
  };

  const stopTimer = () => {
    setIsRunning(false);
    clearInterval(intervalId); // Assuming intervalId is defined in startTimer
  };

  const resetTimer = () => {
    setTime(60);
    setIsRunning(false);
    clearInterval(intervalId); // Clear any existing interval
  };
  useEffect(() => {
    if (time <= 0) {
      stopTimer();
    }
  }, [time]);
  useEffect(() => {
    startTimer();
    // Cleanup function for the effect, ensuring interval is cleared on unmount
    return () => clearInterval(intervalId);
  }, [intervalId]); // Dependency on intervalId

  return (
    <div className=" space-y-4">
      {isCodeWrong === null ? (
        <div className="space-y-6">
          <p className="text-sec-title text-center text-base">
            {intl.formatMessage({ id: "doNotReceivedOTP" })}
            <Button
              type="link"
              className="font-semibold border-none bg-white text-"
              disabled={time > 0 || isLoading}
              onClick={() => {
                reSendOtp();
                resetTimer();
                startTimer();
              }}
            >
              {intl.formatMessage({ id: "reSendOTP" })}
            </Button>
          </p>
          <div className="flex justify-center gap-1 items-baseline">
            <span>{timeSVG}</span>
            <span>00:{time > 0 ? time : 0}</span>
          </div>
        </div>
      ) : isCodeWrong === false ? (
        <p className="text-sec-title text-center">
          {intl.formatMessage({ id: "OTPCorrect" })}
        </p>
      ) : (
        <div className="text-center space-y-6">
          <p className="text-sec-title text-center">
            {intl.formatMessage({ id: "OTPIncorrect" })}
          </p>
          <Button
            type="link"
            className="font-semibold border-none bg-white text-"
            onClick={() => {
              mutate();
            }}
            disabled={isLoading}
          >
            {intl.formatMessage({ id: "reSendOTP" })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Footer;
